import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Eye, Calendar, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const ManagerOffresEnCours = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jobOffers, setJobOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobOffers();
  }, []);

  const fetchJobOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const offers = await response.json();
        // Filter only "en_cours" offers
        const offersEnCours = offers.filter(offer => offer.status === 'en_cours');
        setJobOffers(offersEnCours);
      } else {
        toast.error('Erreur lors du chargement des offres');
      }
    } catch (error) {
      console.error('Error fetching job offers:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToManager = () => {
    navigate('/manager');
  };

  const handleOfferClick = (offer) => {
    navigate(`/manager/offer-detail/${offer.id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Company logos
  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  return (
    <div className="manager-offres-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToManager}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos[user?.company]}
            alt={`Logo ${user?.company}`}
            className="rh-header-logo"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <h1>Offres en cours - {user?.company}</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Section Header */}
            <div className="manager-section-header">
              <h2>Offres en cours</h2>
              <span className="recruitment-counter">{jobOffers.length} offre{jobOffers.length > 1 ? 's' : ''}</span>
            </div>

            {/* Offers Grid */}
            <div className="recruitment-grid">
              {jobOffers.map((offer) => (
                <Card 
                  key={offer.id}
                  className="recruitment-card cursor-pointer group"
                  onClick={() => handleOfferClick(offer)}
                >
                  <CardContent className="recruitment-card-content">
                    <div className="recruitment-card-header">
                      <img
                        src={companyLogos[offer.company]}
                        alt={`Logo ${offer.company}`}
                        className="recruitment-card-logo"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Eye className="recruitment-card-icon" />
                    </div>
                    <h3 className="recruitment-card-title">
                      {offer.company} : {offer.title}
                    </h3>
                    <div className="recruitment-card-info">
                      <div className="recruitment-card-info-item">
                        <User size={16} />
                        <span>Manager : {offer.creator_name || 'Inconnu'}</span>
                      </div>
                      <div className="recruitment-card-info-item">
                        <Calendar size={16} />
                        <span>{formatDate(offer.date_arrivee)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {jobOffers.length === 0 && (
              <div className="empty-state">
                <p>Aucune offre en cours</p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ManagerOffresEnCours;